// The `agents` namespace: module 14's office area (spec 14 §4.2 to §4.4, D-512,
// D-529 ruling 2): the Agent Hub, the agent detail with its policy editor and
// prompt tab, and the action journal. en is the source and ru is translated
// (agents.ru.ts); fr, zh, zh-Hant and hi live in agents.<locale>.ts beside this
// file and are machine-translated and back-translated like every other
// namespace (D-538 ruling 1).
//
// THREE KEY FAMILIES ARE HELD TO THE SERVER:
//   - `tools.<module>.<tool>.name`, one per registered tool, the display name the
//     boot check requires (apps/api/src/platform/agents/agent-tool-name-keys.ts,
//     D-505 ruling 5);
//   - `compensation.<module>.<tool>`, the text a compensatable tool's undo dialog
//     shows (its `reversibility.compensationKey`, D-511 ruling 2);
//   - `outcome.<key>`, a run's outcome (D-515 ruling 6).
// `tools/agents/__tests__/web-dictionary.test.ts` holds the first two to the
// registry both ways, so a tool cannot register without a name and a name
// cannot outlive its tool.
//
// THE AGENT'S OWN WORDS are never in this file: a card's note and a message
// body are the agent's, rendered in the untrusted frame (D-516 ruling 4). The
// frame's label is the only text this namespace puts around them.

import type { Translated } from "../../../i18n-shape";
import { agentsRuDict } from "./agents.ru";

export const agentsEn = {
  nav: {
    section: "Agents",
    aria: "Agents sections",
    hub: "Agents",
    journal: "Journal",
  },
  common: {
    loading: "Loading agents…",
    retry: "Try again",
    requestId: "Request ID",
    loadError: "Could not load this.",
    stale: "Showing the last answer",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    close: "Close",
    discard: "Discard",
    all: "All",
    previous: "Previous",
    next: "Next",
    pageRange: "{{from}}–{{to}}",
    pageOf: "{{from}}–{{to}} of {{total}}",
    sortToggle: "Sort by {{column}}, {{direction}}",
    ascending: "ascending",
    descending: "descending",
    forbiddenTitle: "No access",
    forbiddenBody: "Your role cannot open this page.",
    notFoundTitle: "Agent not found",
    notFoundBody: "It may have been removed, or your role cannot see it.",
  },
  catalog: {
    ops_copilot: { name: "Operations copilot" },
    offer_agent: { name: "Offer agent" },
    planner_agent: { name: "Planning agent" },
    qc_watcher: { name: "Quality watcher" },
    finance_clerk: { name: "Finance clerk" },
  },
  roles: {
    agent_sales: "Sales agent",
    agent_planner: "Planning agent",
    agent_qc: "Quality agent",
    agent_finance: "Finance agent",
    agent_ops: "Operations agent",
  },
  status: {
    enabled: "Enabled",
    paused: "Paused",
    disabled: "Disabled",
    pausedBy: "Paused by {{name}} on {{date}}",
    pausedOn: "Paused on {{date}}",
  },
  hub: {
    title: "Agents",
    lede: "What your agents did, and what they may do alone.",
    tabs: {
      aria: "Agents views",
      agents: "Agents",
      guardrails: "Guardrails",
    },
    emptyTitle: "No agents yet",
    emptyBody: "Agents appear here once your workspace has one.",
    loading: "Loading agents…",
    loadError: "Could not load the agents.",
    actions: "Actions this month",
    actionsOf: "{{used}} of {{cap}}",
    actionsNoCap: "{{used}}, no cap",
    spend: "Spend this month",
    spendOf: "{{used}} of {{cap}}",
    capReached: "Cap reached",
    tokens: "Tokens today",
    tokensOf: "{{used}} of {{budget}}",
    lastRun: "Last run",
    lastRunNone: "Not run yet",
    activity: "Executed actions, last 30 days",
    viewJournal: "View journal",
    configure: "Configure",
    enable: "Enable",
    pause: "Pause",
    toggleLabel: "{{name}} enabled",
    statusSaved: "{{name}} is now {{status}}.",
    banner: {
      injection_one: "A message tried to steer an agent in the last 24 hours.",
      injection: "{{count}} messages tried to steer an agent in the last 24 hours.",
      spend_one: "An agent reached its spend cap in the last 24 hours.",
      spend: "Agents reached their spend cap {{count}} times in the last 24 hours.",
      review: "Review guardrails",
    },
    readOnly: "Read only",
  },
  runStatus: {
    queued: "Queued",
    running: "Running",
    awaiting_approval: "Awaiting approval",
    completed: "Completed",
    failed: "Failed",
    cancelled: "Cancelled",
  },
  trigger: {
    message: "Message",
    event: "Event",
    schedule: "Schedule",
    manual: "Manual",
  },
  outcome: {
    drafted: "Drafted a quote",
    clarify_proposed: "Asked the customer a question",
    status_proposed: "Proposed an order status reply",
    not_a_quote_request: "Not a quote request",
    identity_unresolved: "The sender is not a known customer",
    forwarded_not_processed: "A forwarded message, left for a person",
    no_data_in_scope: "Nothing to act on",
    no_owner: "The customer has no assigned manager",
  },
  guardrails: {
    title: "Guardrails",
    emptyTitle: "No guardrail events",
    emptyBody: "Nothing has been stopped in this period.",
    loading: "Loading guardrail events…",
    loadError: "Could not load the guardrail events.",
    filters: {
      kind: "Kind",
      agent: "Agent",
      from: "From",
      to: "To",
      clear: "Clear filters",
    },
    columns: {
      when: "When",
      kind: "Kind",
      agent: "Agent",
      detail: "Detail",
    },
    kind: {
      prompt_injection_suspected: "Message tried to steer the agent",
      tool_denied_visibility: "Tool refused: no access",
      tool_denied_policy: "Tool refused by policy",
      cap_action_exceeded: "Action cap reached",
      cap_spend_exceeded: "Spend cap reached",
      cap_token_exceeded: "Token budget reached",
      schema_validation_failed: "Agent sent invalid input",
      citation_missing: "Answer without a source",
      scope_violation_blocked: "Outside the agent's scope",
      tool_orphaned: "Tool removed",
      run_limit_exceeded: "Run stopped at its limit",
    },
    detail: {
      pattern: "Matched: {{pattern}}",
      tool: "Tool: {{tool}}",
      capCount: "{{current}} of {{cap}}",
      tokens: "{{used}} of {{budget}} tokens",
      attempt: "Attempt {{attempt}}",
      turnLimit: "Stopped after {{value}} turns",
      wallClock: "Stopped after {{value}} s",
      spend: "{{spend}} of {{cap}}",
    },
    run: "Run",
    runActions: "Actions from this run",
    runNoActions: "This run proposed nothing.",
    openCard: "Open card",
    expand: "Details",
  },
  detail: {
    loading: "Loading the agent…",
    loadError: "Could not load the agent.",
    back: "Agents",
    displayName: "Name",
    rename: "Rename",
    renamePlaceholder: "Leave empty to use the standard name",
    renamed: "Name saved.",
    tabs: {
      aria: "Agent sections",
      policies: "Tool policies",
      prompt: "Prompt",
    },
    memberships: {
      title: "Where it works",
      empty: "No scope yet. The agent cannot act anywhere.",
      group: "Whole group",
      facility: "{{facility}}",
      department: "{{facility}} · {{department}}",
      add: "Add scope",
      remove: "Remove",
      scopeType: "Scope",
      facilityPick: "Facility",
      departmentPick: "Department",
      choose: "Choose…",
      saved: "Scope saved.",
      scope: {
        group: "Group",
        facility: "Facility",
        department: "Department",
      },
    },
    caps: {
      title: "Caps",
      monthlyActions: "Monthly unsupervised actions",
      monthlySpend: "Monthly spend cap ({{currency}})",
      dailyTokens: "Daily token budget",
      saved: "Caps saved.",
      invalid: "Enter a whole number of zero or more.",
      invalidMoney: "Enter an amount of zero or more.",
      ceiling: "Facility admins may allow money tools up to ({{currency}})",
      ceilingSaved: "Ceiling saved.",
    },
    policies: {
      loading: "Loading the tool policies…",
      loadError: "Could not load the tool policies.",
      empty: "This agent has no tools you can see.",
      readOnlyRows: "Read tools run without asking.",
      columns: {
        tool: "Tool",
        autonomy: "Autonomy",
        numbers: "Policy numbers",
        usage: "Last 30 days",
      },
      kind: {
        read: "Read",
        write: "Write",
      },
      reversibility: {
        reversible: "Reversible",
        compensatable: "Compensatable",
        irreversible: "Irreversible",
      },
      usage: "{{executed}} done · {{proposed}} proposed",
      source: {
        declared: "Default",
        explicit: "Set here",
      },
      orphaned: "Tool removed in a platform update. Policy inert.",
      dismiss: "Dismiss",
      dismissed: "Policy dismissed.",
      cap: "Cap ({{currency}})",
      minReliability: "Minimum reliability (%)",
      facilityCap: "{{facility}} cap ({{currency}})",
      addFacility: "Add facility",
      noCaps: "No facility caps set.",
      removeFacility: "Remove",
      spendCapWarning: "This agent cannot act unsupervised until its monthly spend cap is raised.",
      invalidNumber: "Enter a number of zero or more.",
    },
    autonomy: {
      disabled: "Disabled",
      ask: "Ask",
      threshold: "Threshold",
      autonomous_with_cap: "Autonomous with cap",
      autonomous: "Autonomous",
    },
    unsaved: {
      count_one: "{{count}} unsaved change",
      count: "{{count}} unsaved changes",
      item: "{{tool}}: {{from}} → {{to}}",
      itemNumbers: "{{tool}}: policy numbers",
      saved_one: "{{count}} policy saved.",
      saved: "{{count}} policies saved.",
    },
    conflict: {
      title: "Someone else changed this",
      body: "Their change was saved first. Keep theirs, or apply yours on top of it.",
      theirs: "Theirs",
      yours: "Yours",
      keepTheirs: "Keep theirs",
      applyMine: "Apply mine",
    },
    prompt: {
      loading: "Loading the prompt…",
      loadError: "Could not load the prompt.",
      active: "Active prompt",
      version: "Version {{version}}",
      history: "Versions",
      columns: {
        version: "Version",
        source: "Source",
        changelog: "Change",
        created: "Created",
        state: "State",
      },
      source: {
        launch_pack: "Standard",
        platform_update: "Platform update",
        tenant_override: "Your override",
      },
      isActive: "Active",
      activate: "Activate",
      activated: "Version {{version}} is active.",
      override: "Override",
      overrideTitle: "Override the prompt",
      overrideBody: "Your version replaces the standard prompt once activated.",
      promptText: "Prompt",
      changelog: "What changed",
      confirmLabel: "Type {{word}} to confirm",
      create: "Save version",
      created: "Version saved. Activate it to use it.",
      empty: "No prompt versions.",
    },
  },
  journal: {
    toolRemoved: "Tool removed in a platform update",
    title: "Journal",
    lede: "Every action an agent proposed or took.",
    tabs: {
      aria: "Journal views",
      mine: "Needs my decision",
      proposed: "All proposed",
      executed: "Executed",
      rejected: "Rejected",
      failed: "Failed",
      expired: "Expired",
      undone: "Undone",
    },
    empty: {
      mine: "Nothing waiting on you",
      other: "No actions here",
      body: "Cards appear here as the agents work.",
    },
    loading: "Loading the journal…",
    loadError: "Could not load the journal.",
    filters: {
      label: "Filter the journal",
      agent: "Agent",
      tool: "Tool",
      facility: "Facility",
      department: "Department",
      from: "From",
      to: "To",
      impact: "Impact at least ({{currency}})",
      impactNoCurrency: "Impact at least",
      clear: "Clear filters",
    },
    columns: {
      action: "Action",
      agent: "Agent",
      impact: "Impact",
      created: "Proposed",
      expires: "Expires",
      status: "Status",
      decide: "Decide",
    },
    status: {
      proposed: "Proposed",
      approved: "Approved",
      rejected: "Rejected",
      executing: "Executing",
      executed: "Executed",
      failed: "Failed",
      undone: "Undone",
      expired: "Expired without decision",
    },
    impact: {
      count_one: "{{count}} item",
      count: "{{count}} items",
      schedule_one: "{{count}} schedule move",
      schedule: "{{count}} schedule moves",
      message_one: "{{count}} message",
      message: "{{count}} messages",
    },
    countdown: {
      days: "{{days}} d {{hours}} h left",
      hours: "{{hours}} h {{minutes}} min left",
      minutes: "{{minutes}} min left",
      expired: "Expired",
    },
    approve: "Approve",
    reject: "Reject",
    approved: "Approved.",
    approvedExecuting: "Approved. It is running now.",
    rejected: "Rejected.",
    rejectTitle: "Reject this action",
    rejectNote: "Note (optional)",
    note: {
      label: "Agent's note",
      frame: "Written by the agent. Check it before you act.",
    },
    run: {
      label: "Run",
      line: "{{trigger}} · {{status}}",
      error: "Ended with: {{error}}",
    },
    evidence: "Details",
    params: "What it would do",
    result: "Open the result",
    undo: "Undo",
    undoTitle: "Undo this action",
    undoBody: "This reverses what the agent did. The journal keeps both records.",
    undoConfirm: "Undo it",
    undone: "Undone.",
    manualTitle: "Undo by hand",
    manualOpen: "Open the record",
    bulk: {
      count_one: "{{count}} selected",
      count: "{{count}} selected",
      clear: "Clear",
      approve: "Approve selected",
      done_one: "{{count}} approved.",
      done: "{{count}} approved.",
      partial: "{{ok}} approved, {{failed}} refused.",
      select: "Select",
      selectAll: "Select all that can be approved",
      limit: "Up to {{max}} cards of one tool at a time.",
    },
    focus: {
      title: "Opened card",
      close: "Show all",
      missing: "This card is not in your journal.",
    },
    template: {
      openQuote: "Open the quote",
      openOrder: "Open the order",
      body: "Message",
      lines: "Quote lines",
      fill: {
        offcut_combo: "From off-cuts",
        stock_unit: "From a stock unit",
        production: "To be produced",
      },
      perUnit: "{{price}} each",
      alternatives: "Ways to fill each line",
      lineNo: "Line {{no}}",
      pieces_one: "Pieces: {{count}}",
      pieces: "Pieces: {{count}}",
      chosen: "Used in the draft",
      quoteNo: "Quote {{no}}",
      noChannel: "No channel can deliver this",
      marginBreached: "Below the margin guard",
      offList_one: "Off-list lines: {{count}}",
      offList: "Off-list lines: {{count}}",
      total: "Total",
      totalOfCap: "{{total}}, cap {{cap}}",
      reliability: "Payment reliability",
      reliabilityUnknown: "No score",
      reliabilityOf: "{{score}}, minimum {{min}}",
      sends: "Sent by the agent in 7 days",
      sendsOf_one: "{{count}} of {{limit}}",
      sendsOf: "{{count}} of {{limit}}",
      linesCount_one: "Lines: {{count}}",
      linesCount: "Lines: {{count}}",
      orderNo: "Order {{no}}",
      customerGets: "The customer gets:",
      statusPreview: "Order {{no}}: {{status}}",
      openConversation: "Open the conversation",
    },
    redraft: {
      button: "Draft offer",
      busy: "Starting…",
      started: "The agent is drafting again. Its new card appears here.",
      noMessage: "The message this card came from can no longer be found.",
    },
    evidenceKey: {
      impact_count: "Items",
      shortage_count: "Shortages",
      qty_planned: "Planned quantity",
      items: "Items",
      max_autonomous_items: "Most items allowed alone",
      cap: "Cap",
      used: "Used",
      remaining: "Remaining",
    },
    applied: {
      ask: "Asked by its policy",
      forced_ask: "Asked: it could not act alone",
      threshold_pass: "Under the threshold",
      threshold_exceeded_ask: "Over the threshold",
      cap_exceeded_ask: "Over the cap",
      autonomous: "Acted alone",
      autonomous_with_cap: "Acted alone within its cap",
      human_initiated: "Started by a person",
    },
  },
  problems: {
    AGENT_AUTONOMY_ASK_REQUIRED: "This tool needs a person's approval.",
    AGENT_AUTONOMY_CAP_EXCEEDED: "This would pass the agent's cap.",
    AGENT_AUTONOMY_THRESHOLD_EXCEEDED: "This is over the tool's threshold.",
    AGENT_AUTONOMY_THRESHOLD_UNKNOWN: "The threshold could not be measured.",
    AGENT_AUTONOMY_NOT_ALLOWED: "That autonomy is laxer than the tool allows.",
    AGENT_TOOL_NOT_AVAILABLE: "This tool is not available.",
    AGENT_TOOL_PARAMS_INVALID: "The agent sent input the tool refused.",
    AGENT_TOOL_TENANT_PARAM_FORBIDDEN: "The agent named a workspace it may not.",
    AGENT_TOOL_INVARIANT_BROKEN: "The tool stopped to protect your data.",
    AGENT_PRINCIPAL_UNBOUND: "The agent acted without its own identity and was stopped.",
    AGENT_IRREVERSIBLE_NOT_AUTONOMOUS: "An irreversible tool cannot act alone.",
    AGENT_NOT_FOUND: "This agent no longer exists.",
    AGENT_VERSION_CONFLICT: "Someone else changed this agent. Reload and try again.",
    AGENT_PROFILE_UNKNOWN: "That model profile is not available.",
    AGENT_TOOLS_MISSING: "This agent cannot be enabled yet.",
    AGENT_BINDING_OUTSIDE_SCOPE: "You cannot grant a scope outside your own.",
    AGENT_TOOL_NOT_FOUND: "This tool is not on the agent's list.",
    AGENT_POLICY_LAXER_REQUIRES_CONFIG: "Only a group admin can loosen this policy.",
    AGENT_FACILITY_CEILING_EXCEEDED: "This is above the money ceiling set for facility admins.",
    AGENT_POLICY_VERSION_CONFLICT: "Someone else changed this policy.",
    AGENT_POLICY_NOT_ORPHANED: "This policy's tool still exists.",
    AGENT_PROMPT_VERSION_NOT_FOUND: "This prompt version no longer exists.",
    AGENT_PROMPT_ACTIVATION_CONFLICT: "Another version was activated first.",
    AGENT_TRIGGER_INVALID: "The agent cannot start from this.",
    AGENT_RUN_SUBJECT_NOT_FOUND: "The record the agent would act on is not visible to you.",
    AGENT_PAUSED: "This agent is paused.",
    AGENT_TOKEN_BUDGET_EXHAUSTED: "The agent's daily budget is spent.",
    AGENT_PLATFORM_TOKEN_CEILING_REACHED: "Agents are at today's limit. Try again tomorrow.",
    AGENT_RUN_NOT_FOUND: "This run no longer exists.",
    AGENT_RUN_NOT_CANCELLABLE: "This run has already finished.",
    AGENT_IMPACT_FILTER_REQUIRES_VALUE: "Your role cannot filter by amount.",
    AGENT_ACTION_NOT_FOUND: "This card is not in your journal.",
    AGENT_APPROVAL_REQUIRES_FIELD_VISIBILITY: "You cannot approve an amount your role cannot see.",
    AGENT_ACTION_EXPIRED: "This card expired before a decision.",
    AGENT_ACTION_ALREADY_DECIDED: "Someone already decided this card.",
    AGENT_ACTION_NOT_UNDOABLE: "This action cannot be undone here.",
    AGENT_TOOL_DISABLED: "This tool is turned off for this agent.",
    AGENT_RUN_TURN_LIMIT: "The run hit its turn limit and stopped.",
    AGENT_RUN_WALL_CLOCK: "The run took too long and stopped.",
    AGENT_TOOL_NOT_VISIBLE: "The agent cannot use this tool.",
    AGENT_MODEL_UNAVAILABLE: "The agent's model did not answer.",
    AGENT_MODEL_ACCESS_DENIED: "The agent's model could not be reached.",
    AGENT_MODEL_REPLAY_DIVERGED: "The agent's recorded answer did not match.",
    AGENT_RUN_INTERRUPTED: "The run was interrupted.",
    AGENT_TOOL_FAILED_TWICE: "A tool failed twice, so the run stopped.",
    AGENT_TEXT_CARRIES_PRICE: "The message named a price, so it was not sent.",
    AGENT_SUBJECT_UNOWNED: "The customer has no assigned manager.",
    PERMISSION_DENIED: "Your role cannot do this.",
    other: "This was refused.",
  },
  tools: {
    catalogue: {
      add_product_conversion: { name: "Add a unit conversion" },
      create_product_draft: { name: "Draft a product" },
      draft_bom_version: { name: "Draft a BOM version" },
      draft_routing_version: { name: "Draft a routing version" },
      explain_cost: { name: "Explain a cost" },
      get_bom_tree: { name: "Read a BOM tree" },
      get_change_history: { name: "Read a change history" },
      get_product: { name: "Read a product" },
      get_routing: { name: "Read a routing" },
      search_products: { name: "Search products" },
      set_component_scrap: { name: "Set component scrap" },
      start_cost_rollup: { name: "Start a cost roll-up" },
      where_used: { name: "Find where a part is used" },
    },
    inventory: {
      adjust_stock: { name: "Adjust stock" },
      create_transfer_draft: { name: "Draft a transfer" },
      flag_quarantine: { name: "Quarantine stock" },
      get_atp: { name: "Read available-to-promise" },
      get_stock_unit: { name: "Read a stock unit" },
      list_in_transit: { name: "List stock in transit" },
      list_open_variances: { name: "List open count variances" },
      lookup_stock: { name: "Look up stock" },
      release_reservation: { name: "Release a reservation" },
      request_cycle_count: { name: "Request a cycle count" },
      reserve_stock: { name: "Reserve stock" },
      trace_lot: { name: "Trace a lot" },
    },
    messaging: {
      check_send_viability: { name: "Check a message can be delivered" },
      get_thread: { name: "Read a conversation" },
      send_text: { name: "Send a message" },
    },
    onboarding: {
      apply_column_mapping: { name: "Apply an import column mapping" },
      commit_import: { name: "Commit an import" },
      fix_import_rows: { name: "Fix import rows" },
      generate_qr_kit: { name: "Generate the QR kit" },
      get_checklist: { name: "Read the go-live checklist" },
      get_import_errors: { name: "Read import errors" },
      get_import_job: { name: "Read an import" },
      get_status: { name: "Read the setup status" },
      get_terminology: { name: "Read the terminology" },
      list_packs: { name: "List industry packs" },
      preview_pack: { name: "Preview an industry pack" },
      propose_column_mapping: { name: "Propose an import column mapping" },
      propose_plant_map: { name: "Propose a plant map" },
      run_validation: { name: "Validate an import" },
      waive_checklist_item: { name: "Waive a checklist item" },
    },
    planning: {
      accept_action_message: { name: "Accept an MRP message" },
      approve_mps: { name: "Approve the MPS" },
      commit_promise: { name: "Commit a delivery promise" },
      convert_planned_orders: { name: "Convert planned orders" },
      create_scenario: { name: "Create a what-if scenario" },
      diff_scenario: { name: "Compare a scenario" },
      explain_planned_order: { name: "Explain a planned order" },
      firm_planned_orders: { name: "Firm planned orders" },
      get_action_messages: { name: "Read MRP messages" },
      get_capacity_load: { name: "Read the capacity load" },
      get_demand_pool: { name: "Read the demand pool" },
      get_mps: { name: "Read the MPS" },
      get_promises: { name: "Read delivery promises" },
      get_schedule: { name: "Read the schedule" },
      probe_promise: { name: "Check a delivery date" },
      promote_scenario: { name: "Promote a scenario" },
      quote_promise: { name: "Quote a delivery promise" },
      refresh_demand: { name: "Refresh the demand pool" },
      reschedule_operation: { name: "Move an operation" },
      run_mrp: { name: "Run MRP" },
    },
    production: {
      cancel_work_order: { name: "Cancel a work order" },
      create_work_order: { name: "Create a work order" },
      get_defect_handoff: { name: "Read a defect hand-off" },
      get_machine_drift: { name: "Read machine drift" },
      get_shift_scoreboard: { name: "Read the shift scoreboard" },
      get_wo_cost_summary: { name: "Read a work order's cost" },
      get_work_order: { name: "Read a work order" },
      list_defect_handoffs: { name: "List defect hand-offs" },
      list_open_conflicts: { name: "List open floor conflicts" },
      list_work_orders: { name: "List work orders" },
      raise_drift_exception: { name: "Raise a drift exception" },
      release_work_order: { name: "Release a work order" },
      request_defect_disposition: { name: "Request a defect decision" },
      set_priority: { name: "Set a work order's priority" },
    },
    quality: {
      create_inspection: { name: "Create an inspection" },
      draft_coa: { name: "Draft a certificate of analysis" },
      execute_disposition: { name: "Carry out a hold decision" },
      get_coa_document: { name: "Read a certificate of analysis" },
      get_failure_pareto: { name: "Read the failure Pareto" },
      get_fpy: { name: "Read first-pass yield" },
      get_hold: { name: "Read a quality hold" },
      get_inspection: { name: "Read an inspection" },
      get_shift_scoreboard: { name: "Read the quality scoreboard" },
      get_ship_readiness: { name: "Check readiness to ship" },
      get_supplier_receiving_stats: { name: "Read supplier receiving results" },
      issue_coa: { name: "Issue a certificate of analysis" },
      list_active_holds: { name: "List active quality holds" },
      list_open_inspections: { name: "List open inspections" },
      place_hold: { name: "Place a quality hold" },
      propose_disposition: { name: "Propose a hold decision" },
      propose_guided_cut: { name: "Propose a guided cut" },
      request_rework: { name: "Request rework" },
    },
    sales: {
      check_fulfillment_options: { name: "Check fulfilment options" },
      draft_quote: { name: "Draft a quote" },
      get_customer_360: { name: "Read a customer" },
      get_customer_pipeline: { name: "Read a customer's deals" },
      get_order_status: { name: "Read an order's status" },
      get_quote: { name: "Read a quote" },
      lookup_customer_by_identity: { name: "Find the customer behind a message" },
      reply_order_status: { name: "Reply with an order's status" },
      resolve_price: { name: "Price a product" },
      send_quote: { name: "Send a quote" },
    },
    waste: {
      get_summary: { name: "Read the waste summary" },
      record_event: { name: "Record a waste event" },
      set_disposition: { name: "Set a waste disposition" },
    },
  },
  compensation: {
    catalogue: {
      add_product_conversion: "Change or remove the conversion on the product.",
      create_product_draft: "Discard the draft product in the catalogue.",
      draft_bom_version: "Discard the draft BOM version.",
      draft_routing_version: "Discard the draft routing version.",
      set_component_scrap: "Set the scrap back on the draft BOM line.",
      start_cost_rollup: "Leave the cost run unreleased, or discard it.",
    },
    inventory: {
      adjust_stock: "Reverse the adjustment in the movements ledger.",
      create_transfer_draft: "Cancel the draft transfer.",
      flag_quarantine: "Release the lot from quarantine.",
      release_reservation: "Reserve the stock again for its demand.",
      request_cycle_count: "Cancel the count.",
    },
    onboarding: {
      apply_column_mapping: "Change the column mapping on the import.",
      commit_import: "Correct or archive the records the import created.",
      fix_import_rows: "Edit the rows again on the import.",
      generate_qr_kit: "Ignore the batch. Printing it changes nothing else.",
      propose_column_mapping: "Change the column mapping on the import.",
      propose_plant_map: "Edit the draft plant map in setup.",
      run_validation: "Validate the import again after fixing it.",
      waive_checklist_item: "Reopen the item on the go-live checklist.",
    },
    planning: {
      accept_action_message: "Change the planned order back on the planned orders screen.",
      approve_mps: "Edit the MPS and approve the new version.",
      commit_promise: "Withdraw the promise on the promises screen.",
      convert_planned_orders: "Cancel the draft work orders and transfers it created.",
      create_scenario: "Discard the scenario.",
      firm_planned_orders: "Cancel the firmed planned orders that are not wanted.",
      promote_scenario: "Remove the calendar changes it wrote, then solve again.",
      quote_promise: "Let the quoted promise lapse. It holds nothing.",
      refresh_demand: "Nothing to undo. The next refresh replaces it.",
      reschedule_operation: "Move the operation back on the schedule board.",
      run_mrp: "Nothing to undo. The next MRP run replaces it.",
    },
    production: {
      create_work_order: "Cancel the draft work order.",
      raise_drift_exception: "Close the exception.",
      release_work_order: "Cancel the work order if it should not run.",
      request_defect_disposition: "Withdraw the proposal on the defect hand-off.",
      set_priority: "Set the priority back on the work order.",
    },
    quality: {
      create_inspection: "Cancel the inspection.",
      draft_coa: "Discard the draft certificate.",
      place_hold: "Release the hold from the quarantine board.",
      propose_disposition: "Clear the proposed decision on the hold.",
      propose_guided_cut: "Clear the proposed cut on the inspection.",
      request_rework: "Complete or cancel the reopened operation on the work order.",
    },
    sales: {
      draft_quote: "Cancel the draft quote.",
      send_quote: "Cancel the quote to withdraw its link, or save a new version and send it.",
    },
    waste: {
      record_event: "Reverse the event in the waste ledger.",
      set_disposition: "Change the disposition on the waste event.",
    },
  },
} as const;

export type AgentsDict = Translated<typeof agentsEn>;

export const agentsRu: AgentsDict = agentsRuDict;
